import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { ArchetypeId } from '@llura/types';
import { api } from '../lib/api-client';
import { syncFireAndForget } from '../lib/sync';

/**
 * Runtime mode value used throughout the web app. After the v3 migration
 * everything in this store is an archetype — legacy IDs only ever exist on
 * disk between `migrate` and the first read.
 */
export type ModeId = ArchetypeId;
export type LevelId = 1 | 2 | 3;

export interface SessionRecord {
  mode: ModeId;
  level: LevelId;
  points: number;
  at: number;
}

export interface DailyRingsState {
  /** YYYY-MM-DD in the user's local timezone. Drives the daily reset. */
  date: string;
  mind: number;
  muscle: number;
  sensation: number;
  target: { mind: number; muscle: number; sensation: number };
}

export interface ProgressState {
  devotionLevel: number;
  sessionsCompleted: number;
  history: SessionRecord[];
  lastSessionAt: number | null;
  unlockedModes: ModeId[];

  /** YYYY-MM-DD -> archetype chosen on that day. Trimmed to the last 90 days. */
  selectedArchetypeByDay: Record<string, ArchetypeId>;
  dailyRings: DailyRingsState;

  completeSession: (mode: ModeId, level: LevelId, points: number) => void;
  addPoints: (points: number) => void;
  setTodayArchetype: (id: ArchetypeId) => void;
  hydrate: (input: {
    devotionLevel: number;
    sessionsCompleted: number;
    lastSessionAt: number | null;
    unlockedModes: ModeId[];
    history: SessionRecord[];
  }) => void;
  reset: () => void;
}

const DEFAULT_RING_TARGET = { mind: 1, muscle: 1, sensation: 1 } as const;
const HISTORY_DAY_RETENTION = 90;

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function emptyRingsFor(date: string): DailyRingsState {
  return {
    date,
    mind: 0,
    muscle: 0,
    sensation: 0,
    target: { ...DEFAULT_RING_TARGET },
  };
}

function ringKeyForLevel(level: LevelId): 'mind' | 'muscle' | 'sensation' {
  if (level === 1) return 'mind';
  if (level === 2) return 'muscle';
  return 'sensation';
}

function trimSelectionMap(
  map: Record<string, ArchetypeId>,
): Record<string, ArchetypeId> {
  const dates = Object.keys(map).sort().reverse();
  if (dates.length <= HISTORY_DAY_RETENTION) return map;
  const keep = new Set(dates.slice(0, HISTORY_DAY_RETENTION));
  return Object.fromEntries(Object.entries(map).filter(([k]) => keep.has(k)));
}

const initial = {
  devotionLevel: 0,
  sessionsCompleted: 0,
  history: [] as SessionRecord[],
  lastSessionAt: null as number | null,
  unlockedModes: [
    'priestess',
    'sensualist',
    'huntress',
    'queen',
    'enchantress',
  ] as ModeId[],
  selectedArchetypeByDay: {} as Record<string, ArchetypeId>,
  dailyRings: emptyRingsFor(todayISO()),
};

/**
 * Maps every historical mode id we have ever persisted onto an archetype.
 * Pass-through for current archetype ids. Used by the v2→v3 migration and
 * defensively at API boundaries.
 */
const LEGACY_MODE_MAP: Record<string, ArchetypeId> = {
  // v1 → v2 legacy
  'pleasure-taskmaster': 'queen',
  edged: 'enchantress',
  'sensory-quest': 'sensualist',
  // v2 → v3 legacy (the three Paths)
  structured: 'queen',
  edging: 'enchantress',
  exploratory: 'sensualist',
};

const ARCHETYPE_IDS_SET = new Set<ArchetypeId>([
  'priestess',
  'sensualist',
  'huntress',
  'queen',
  'enchantress',
]);

function migrateMode(id: string): ArchetypeId {
  if (ARCHETYPE_IDS_SET.has(id as ArchetypeId)) return id as ArchetypeId;
  return LEGACY_MODE_MAP[id] ?? 'queen';
}

export const useProgressStore = create<ProgressState>()(
  persist(
    (set) => ({
      ...initial,
      completeSession: (mode, level, points) => {
        set((s) => {
          const now = Date.now();
          const today = todayISO();
          const baseRings =
            s.dailyRings.date === today ? s.dailyRings : emptyRingsFor(today);
          const key = ringKeyForLevel(level);
          const nextValue = Math.min(baseRings[key] + 1, baseRings.target[key]);
          return {
            devotionLevel: s.devotionLevel + points,
            sessionsCompleted: s.sessionsCompleted + 1,
            lastSessionAt: now,
            history: [{ mode, level, points, at: now }, ...s.history].slice(0, 50),
            dailyRings: { ...baseRings, [key]: nextValue },
          };
        });
        syncFireAndForget(() => api.progress.recordSession({ mode, level, points }));
      },
      addPoints: (points) =>
        set((s) => ({ devotionLevel: s.devotionLevel + points })),
      setTodayArchetype: (id) =>
        set((s) => {
          const today = todayISO();
          const nextMap = trimSelectionMap({ ...s.selectedArchetypeByDay, [today]: id });
          const sameDayRings =
            s.dailyRings.date === today ? s.dailyRings : emptyRingsFor(today);
          return { selectedArchetypeByDay: nextMap, dailyRings: sameDayRings };
        }),
      hydrate: (input) =>
        set({
          devotionLevel: input.devotionLevel,
          sessionsCompleted: input.sessionsCompleted,
          lastSessionAt: input.lastSessionAt,
          unlockedModes: input.unlockedModes,
          history: input.history.slice(0, 50),
        }),
      reset: () => set({ ...initial, dailyRings: emptyRingsFor(todayISO()) }),
    }),
    {
      name: 'llura.progress',
      storage: createJSONStorage(() => localStorage),
      version: 3,
      migrate: (persisted, version) => {
        if (!persisted || typeof persisted !== 'object') return initial;
        const legacy = persisted as Partial<ProgressState> & {
          obediencePoints?: number;
        };
        const baseDevotion =
          legacy.devotionLevel ?? legacy.obediencePoints ?? 0;
        const baseSessions = legacy.sessionsCompleted ?? 0;
        const baseLast = legacy.lastSessionAt ?? null;
        const baseHistory = (legacy.history ?? []).map((r) => ({
          ...r,
          mode: migrateMode(r.mode as unknown as string),
        }));
        const baseUnlocked = (legacy.unlockedModes ?? []).map((m) =>
          migrateMode(m as unknown as string),
        );
        // Dedupe unlockedModes after mapping (multiple legacy ids can collapse onto the same archetype).
        const dedupedUnlocked = Array.from(new Set(baseUnlocked));
        const baseSelectionMap = (legacy.selectedArchetypeByDay ?? {}) as Record<
          string,
          string
        >;
        const migratedSelection: Record<string, ArchetypeId> = {};
        for (const [k, v] of Object.entries(baseSelectionMap)) {
          migratedSelection[k] = migrateMode(v);
        }
        return {
          ...initial,
          devotionLevel: baseDevotion,
          sessionsCompleted: baseSessions,
          lastSessionAt: baseLast,
          history: baseHistory,
          unlockedModes:
            dedupedUnlocked.length > 0 ? dedupedUnlocked : initial.unlockedModes,
          selectedArchetypeByDay: trimSelectionMap(migratedSelection),
          dailyRings:
            version >= 3 && legacy.dailyRings && legacy.dailyRings.date === todayISO()
              ? legacy.dailyRings
              : emptyRingsFor(todayISO()),
        };
      },
    },
  ),
);

/** Selector hook — returns today's archetype or null. */
export function useTodayArchetype(): ArchetypeId | null {
  return useProgressStore((s) => s.selectedArchetypeByDay[todayISO()] ?? null);
}
